/**
 * @file RepRepliesImportModal.js
 * @version 1.6.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-17
 * @modified 2026-10-28
 * @description Pricing & Costing D7 (Cam: "the rep's filled-in spreadsheet can
 *              be dropped onto the screen, which fills the prices in"): choose
 *              the rep's price request spreadsheet, see each price against its
 *              order lines, untick any to leave, then import. Imported prices
 *              become a "Rep reply" option on each line — nothing is written to
 *              the orders until someone confirms (D6). Lines are found by the
 *              spreadsheet's hidden Ref column.
 *
 *              1.1.0 (Cam, 19 Oct — "a table for the user to review before
 *              accepting … select all, select some and approve or hold"): tick
 *              all or some, then Approve (the sale price is confirmed at the
 *              rep's price — audited, Undo-able from the screen), Hold (into Held
 *              rep prices, for a second check) or Keep as options (as before).
 *              Approve and Hold need migration pricing_costing_rep_review.sql.
 *
 *              1.2.0 (Cam, 19 Oct — "when reading back in, those groupings need
 *              to be processed and checked by users"): each spreadsheet row is a
 *              GROUP — one request covering several order lines (its Ref) — shown
 *              as the rep saw it (customer, product, unit, the cost it was asked
 *              at, the orders and lines it covers, the rep's price and notes),
 *              its lines underneath, to tick or untick as a group or one by one.
 *              A line that has changed since it was asked (gone, invoiced,
 *              already priced, another customer or product, another cost —
 *              utils/pricingCosting.repReplyLineCheck) is flagged and starts
 *              unticked with the reason. `lineFacts` (order line id → { cost })
 *              is each line's cost as the screen shows it now. Each reply is
 *              still stored per line.
 *
 *              1.3.0 (Cam, 20 Oct 2026): a line whose sale price is already
 *              CONFIRMED here, or whose contract price in force differs from the
 *              rep's, is a question before anything is stored — "Confirmed £x
 *              (source — by, DD/MM)" / "Contract now £y" against "Rep's price £z",
 *              answered per row (or per line where they differ): Keep confirmed
 *              (the reply not stored), Use contract (the line's sale confirmed at
 *              the contract) or Use the rep's (stored and confirmed at the
 *              rep's) — with "Keep every confirmed price" / "Use every rep price".
 *              Nothing is imported until every ticked question is answered.
 *              And each row can ALSO be made a contract price (off by default):
 *              a New contract (a period of that category, or From / To dates;
 *              the order's delivery terms; this customer or its pricing group)
 *              or Add to an existing contract of the customer's, choosing what
 *              happens to each product it already holds (keep, put on hold,
 *              archive, replace). "What will be written" lists it all before
 *              anything is. Writes: services/repReplyContractService.js.
 *
 *              1.4.0 (Cam, 21 Oct 2026):
 *              - "add two different items to one new contract … customers have
 *                contract pricing for a group of products (same product from
 *                different suppliers so different item codes)": a new contract
 *                can take other rows' products ("Add another row's product…") —
 *                ONE contract, one row per product sharing a contract group, its
 *                period / dates and scope shared (`shared`, keyed A, B, …).
 *                Rows of another customer or category, or the same product
 *                twice, are refused with the reason.
 *              - "still allow the user to click accept / import, but clearly
 *                show the things that need attention and filter the rows to
 *                just those": Approve / Hold / Keep as options stay usable; a
 *                row or line that needs something (a question unanswered, a
 *                line changed since and unticked, a contract not finished) is
 *                marked in the error colour with why and is NOT written — the
 *                rest is, after one question saying how many are left. A
 *                "Needs attention (n)" filter (on when there are any) shows
 *                just those rows; "Show all" goes back.
 *              - the summary at the end names each line not priced by its SO,
 *                PO, customer, delivery date, product and quantity, with why.
 *              - the sticky table header sits flush with the top of the
 *                scrolling body (rows no longer show above it).
 *
 *              1.5.0 (Cam, 28 Oct 2026 — O2: "Don't take the qty or supplied
 *              qty from orders / lines when generating contract pricing from
 *              rep pricing xlsx imports, just leave qty blank by default and
 *              inform user of this in case they do wish to set a tonnage there
 *              and there (give the option) or later"): a new contract has a
 *              Quantity field, BLANK by default, saying plainly that it is
 *              not taken from the orders (their total shown beside it) and can
 *              be set now or later on the contract. One quantity for a
 *              contract of several products (shared, as its dates are).
 *
 *              1.6.0 (Cam, 28 Oct 2026 — N4): the contract panel and its rules
 *              moved, unchanged, to ContractFromPricesPanel — shared with
 *              Pricing & Costing's "Contract these prices" window, so the two
 *              ways of making a contract can never part (the names this file
 *              exported are still exported from here). A row the rep marked
 *              "Make this a contract? (Y)" (`contract_requested`) opens on New
 *              contract with "Rep asks for a contract" — still the office's
 *              decision; the request is sent with the row and kept on its
 *              lines unless a contract is made from them now.
 */

import React, { useMemo, useState } from 'react';
import Modal from './Modal';
import ConfirmDialog from './ConfirmDialog';
import TickCheckbox from './TickCheckbox';
import ContractFromPricesPanel, {
  offerOn, settingsOf, sharedMembers, nextLabel, offerIncomplete, contractProblem, defaultOthers,
  quantityTyped, joinContract, contractsPayload, contractSummary, openedOffer
} from './ContractFromPricesPanel';
import LoadingSpinner from './LoadingSpinner';
import { useSlideOutButton } from './SlideOutButton';
import { API_URL } from '../config';
import { formatDate } from '../utils/dateFormatters';
import { formatQuantity, formatCount } from '../utils/numberFormatters';
import {
  money, repReplyLineCheck, repReplyConflict, bulkConflictAnswer, confirmedText,
  REP_CONFLICT_LABELS
} from '../utils/pricingCosting';
import './PricingCosting.css';

// The contract rules live with the shared panel (1.6.0); still exported from
// here for whoever reads them from the import.
export { settingsOf, sharedMembers, nextLabel, offerIncomplete, contractProblem, defaultOthers, quantityTyped, joinContract };

/** A line that cannot take a reply at all: gone, or invoiced. */
const hardBlock = (l) => (l.missing ? 'No longer on any order' : l.invoiced ? `Invoiced (${l.invoiced})` : null);
const lineKey = (i, id) => `${i}:${id}`;
const qtyText = (l) => {
  if (l.quantity === null || l.quantity === undefined) return '';
  if (!l.unit) return formatQuantity(l.quantity);
  if (l.unit === 'T') return `${formatQuantity(l.quantity)} T`;
  return `${formatCount(l.quantity)} × ${l.unit}`;
};

/**
 * Each row's lines with what has changed since: `{ ...line, block, check }`.
 * The cost now is the screen's (lineFacts), else one confirmed here.
 */
export function checkedRows(read, lineFacts = {}) {
  return (read?.rows || []).map(r => ({
    ...r,
    lines: (r.lines || []).map(l => {
      const facts = lineFacts[l.id] || lineFacts[String(l.id)] || null;
      const costNow = facts && facts.cost !== undefined && facts.cost !== null ? facts.cost : (l.cost_confirmed ?? null);
      return { ...l, block: hardBlock(l), check: repReplyLineCheck(r, l, costNow) };
    })
  }));
}

/** The lines ticked to start with: a priced row's lines with nothing changed. */
export function initialTicks(rows) {
  const t = {};
  rows.forEach((r, i) => r.lines.forEach(l => { t[lineKey(i, l.id)] = !!r.price && !l.block && l.check.ok; }));
  return t;
}


/**
 * What needs attention (1.4.0) — written for nothing until it is put right:
 *   a ticked line with a question unanswered; a line changed since the request
 *   and left unticked; every ticked line of a row whose contract is not right.
 * @returns {{ rows: Object<number, string[]>, lines: Object<string, string>, short: Object<string, string>, count: number }}
 *          `lines` say why in full (the summary), `short` on the line itself.
 */
export function attentionOf({ rows, ticked, conflicts, answers, offers = {}, shared = {} }) {
  const out = { rows: {}, lines: {}, short: {}, count: 0 };
  rows.forEach((r, i) => {
    if (!r.price) return;
    const reasons = [];
    const cp = contractProblem(i, rows, offers, shared);
    if (cp) reasons.push(`Contract: ${cp}`);
    let unanswered = 0;
    let changed = 0;
    r.lines.forEach(l => {
      if (l.block) return;
      const k = lineKey(i, l.id);
      if (ticked[k] && conflicts[k] && !answers[k]) {
        out.lines[k] = 'Answer the question: which price to keep';
        out.short[k] = 'Needs attention: answer which price to keep';
        unanswered += 1;
      } else if (ticked[k] && cp) {
        out.lines[k] = `Its contract is not right: ${cp}`;
        out.short[k] = 'Needs attention: its contract is not right (above)';
      } else if (!ticked[k] && !l.check.ok) {
        out.lines[k] = `Changed since the request — ${l.check.reasons.join('; ')} — tick it to import it anyway`;
        out.short[k] = 'Needs attention: unticked — tick it to import it anyway';
        changed += 1;
      }
    });
    if (unanswered) reasons.push(`${unanswered} question${unanswered === 1 ? '' : 's'} to answer`);
    if (changed) reasons.push(`${changed} line${changed === 1 ? '' : 's'} changed since the request — unticked`);
    if (reasons.length) out.rows[i] = reasons;
  });
  out.count = Object.keys(out.lines).length;
  return out;
}

/** The lines of row i that will be written: ticked, usable, answered, its contract right. */
export function writableIds(i, { rows, ticked, conflicts, answers, offers = {}, shared = {} }) {
  const r = rows[i];
  if (!r || !r.price) return [];
  if (contractProblem(i, rows, offers, shared)) return [];
  return r.lines
    .filter(l => !l.block && ticked[lineKey(i, l.id)])
    .filter(l => { const k = lineKey(i, l.id); return !(conflicts[k] && !answers[k]); })
    .map(l => l.id);
}

/**
 * The questions each ticked-to-be line raises: lineKey → repReplyConflict(…).
 * A row being made a contract price at the rep's has no contract question.
 */
export function conflictsOf(rows, offers = {}) {
  const out = {};
  rows.forEach((r, i) => {
    if (!r.price) return;
    const contractWillBe = offerOn(r, offers[i]) ? r.price : undefined;
    r.lines.forEach(l => {
      if (l.block) return;
      const q = repReplyConflict(l, r.price, { contractWillBe });
      if (q) out[lineKey(i, l.id)] = q;
    });
  });
  return out;
}

/**
 * The body POST /rep-replies takes — choices and contracts only when there
 * are any. Lines that need attention are left out (1.4.0); a shared new
 * contract goes as one contract with `products` (ContractFromPricesPanel
 * .contractsPayload). A row the rep asked to be made a contract carries
 * `contract_requested` (1.6.0) — the server keeps the request on its lines
 * unless a contract is made from them now.
 */
export function buildPayload({ fileName, rows, ticked, answers, conflicts, offers, shared = {}, action }) {
  const out = { file_name: fileName, action, rows: [] };
  const ctx = { rows, ticked, conflicts, answers, offers, shared };
  rows.forEach((r, i) => {
    const ids = writableIds(i, ctx);
    if (!ids.length) return;
    const row = { lineIds: ids, price: r.price, notes: r.notes };
    const choices = {};
    ids.forEach(id => { const k = lineKey(i, id); if (conflicts[k] && answers[k]) choices[id] = answers[k]; });
    if (Object.keys(choices).length) row.choices = choices;
    if (r.contract_requested) row.contract_requested = true;
    out.rows.push(row);
  });
  const contracts = contractsPayload({ rows, offers, shared, idsOf: (i) => writableIds(i, ctx) });
  if (contracts.length) out.contracts = contracts;
  return out;
}

/** "What will be written", in sentences, for the lines that will be written. */
export function writeSummary({ rows, ticked, answers, conflicts, offers, shared = {}, action }) {
  const ctx = { rows, ticked, conflicts, answers, offers, shared };
  const idsOf = (i) => writableIds(i, ctx);
  const lines = contractSummary({ rows, offers, shared, idsOf });
  let plain = 0; const n = { keep: 0, contract: 0, rep: 0 };
  rows.forEach((r, i) => {
    idsOf(i).forEach(id => {
      const k = lineKey(i, id);
      if (conflicts[k]) { if (answers[k]) n[answers[k]] += 1; } else plain += 1;
    });
  });
  const s = (k) => (k === 1 ? '' : 's');
  if (n.keep) lines.push(`${n.keep} line${s(n.keep)} keep their confirmed price — the rep's is not stored for them.`);
  if (n.contract) lines.push(`${n.contract} line${s(n.contract)} confirmed at the contract price — the rep's is not stored for them.`);
  if (n.rep) lines.push(`${n.rep} line${s(n.rep)} confirmed at the rep's price (stored as approved).`);
  if (plain) {
    lines.push(action === 'approve' ? `${plain} line${s(plain)}: sale price confirmed at the rep's.`
      : action === 'hold' ? `${plain} line${s(plain)}: the rep's price held (Held rep prices).`
        : `${plain} line${s(plain)}: the rep's price kept as an option ("Rep reply").`);
  }
  const askedRows = rows.filter((r, i) => r.contract_requested && idsOf(i).length && !offerOn(r, offers[i])).length;
  if (askedRows) lines.push(`${askedRows} row${s(askedRows)} the rep asked to be made a contract — not made now: the request stays on ${askedRows === 1 ? 'its' : 'their'} lines ("Contract asked for") for later.`);
  return lines;
}

/** A line as the end summary names it. */
function describe(l, fromServer) {
  const x = { ...l, ...(fromServer || {}) };
  return {
    id: x.id, order_number: x.order_number || null, po_number: x.po_number || null,
    account_no: x.account_no || null, customer: x.customer || x.account_name || null,
    delivery_date: x.delivery_date || null, product_code: x.product_code || x.item_code || null,
    product: x.product || x.item_name || null, quantity: x.quantity ?? null, unit: x.unit || null, missing: !!x.missing
  };
}

/**
 * Every line of the import NOT priced, and why (1.4.0 — Cam: "show the
 * PO/SO/customer/date information and not just the product code and row
 * number"): left out needing attention, refused by the save, a contract not
 * written, no price from the rep, gone or invoiced, unticked.
 */
export function notDoneList({ rows, ticked, attention, results = [], contracts = [] }) {
  const out = [];
  const seen = new Set();
  const byId = new Map();
  rows.forEach(r => r.lines.forEach(l => byId.set(Number(l.id), l)));
  const push = (line, why, kind, server) => {
    const k = `${line.id}|${why}`;
    if (seen.has(k)) return;
    seen.add(k);
    out.push({ ...describe(line, server), why, kind });
  };
  rows.forEach((r, i) => {
    r.lines.forEach(l => {
      const k = lineKey(i, l.id);
      if (l.block) push(l, l.block, 'block');
      else if (!r.price) push(l, r.priceText ? `The rep wrote "${r.priceText}" — not a price` : 'No price from the rep', 'no_price');
      else if (attention.lines[k]) push(l, attention.lines[k], 'attention');
      else if (!ticked[k]) push(l, 'Left unticked', 'unticked');
    });
  });
  results.filter(x => x.outcome && x.outcome !== 'confirmed' && x.outcome !== 'held').forEach(x => {
    const id = Number(x.line_id || String(x.key || '').replace(/^ol:/, ''));
    const l = byId.get(id) || { id };
    push(l, x.message || 'Not done', 'refused', x.line);
  });
  contracts.filter(c => !c.ok).forEach(c => {
    const lines = (c.lines && c.lines.length) ? c.lines : [];
    const why = `Contract not written — ${c.message}`;
    if (lines.length) lines.forEach(sl => push(byId.get(Number(sl.id)) || sl, why, 'contract', sl));
    else push({ id: `row ${(c.rows || [c.row]).join(', ')}` }, why, 'contract');
  });
  return out;
}

function RepRepliesImportModal({ onClose, onDone, reviewReady = false, lineFacts = {} }) {
  const [read, setRead] = useState(null);
  const [ticked, setTicked] = useState({});
  const [openRows, setOpenRows] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [answers, setAnswers] = useState({});
  const [offers, setOffers] = useState({});
  // Shared new contracts (1.4.0): label → { periodId, useDates, start, end, scope }.
  const [shared, setShared] = useState({});
  // "Needs attention" filter: the rows it showed when it was switched on.
  const [attentionOnly, setAttentionOnly] = useState(false);
  const [pinned, setPinned] = useState([]);
  const [asking, setAsking] = useState(null);
  const [result, setResult] = useState(null);

  const rows = useMemo(() => checkedRows(read, lineFacts), [read, lineFacts]);
  const conflicts = useMemo(() => conflictsOf(rows, offers), [rows, offers]);
  const attention = useMemo(() => attentionOf({ rows, ticked, conflicts, answers, offers, shared }),
    [rows, ticked, conflicts, answers, offers, shared]);

  const readFile = async (file) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/pricing-costing/rep-replies/read`, { method: 'POST', credentials: 'include', body: fd });
      const body = await res.json();
      if (!res.ok || !body.success) throw new Error(body.error || 'The spreadsheet could not be read');
      const checked = checkedRows(body, lineFacts);
      const ticks = initialTicks(checked);
      setRead(body);
      setAnswers({});
      // A row the rep marked "Make this a contract? (Y)" opens on New contract (1.6.0).
      setOffers(Object.fromEntries(checked.map((r, i) => [i, r.contract_requested ? openedOffer(r) : null]).filter(([, o]) => o)));
      setShared({});
      setTicked(ticks);
      // A group with a line changed since opens, so its reason is seen.
      setOpenRows(Object.fromEntries(checked.map((r, i) => [i, r.lines.length > 1 && r.lines.some(l => !l.check.ok)])));
      // Start on what needs attention, when anything does.
      const startOffers = Object.fromEntries(checked.map((r, i) => [i, r.contract_requested ? openedOffer(r) : null]).filter(([, o]) => o));
      const att = attentionOf({ rows: checked, ticked: ticks, conflicts: conflictsOf(checked, startOffers), answers: {}, offers: startOffers, shared: {} });
      const need = Object.keys(att.rows).map(Number);
      setPinned(need);
      setAttentionOnly(need.length > 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const ctx = { rows, ticked, conflicts, answers, offers, shared };
  const lineCount = rows.reduce((n, r, i) => n + writableIds(i, ctx).length, 0);
  const leftOut = attention.count;

  const showAttention = (on) => {
    setPinned(Object.keys(attention.rows).map(Number));
    setAttentionOnly(on);
  };

  const save = async (action) => {
    setAsking(null);
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/pricing-costing/rep-replies`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(buildPayload({ fileName: read.file_name, rows, ticked, answers, conflicts, offers, shared, action }))
      });
      const body = await res.json();
      if (!res.ok || !body.success) throw new Error(body.error || 'Nothing was imported');
      const n = body.saved || 0;
      const results = body.results || [];
      const confirmedNow = results.filter(r => r.outcome === 'confirmed');
      const s = (k) => (k === 1 ? '' : 's');
      const parts = [];
      if (action === 'approve') parts.push(`${body.approved} rep price${s(body.approved)} approved — the sale prices are confirmed`);
      else if (action === 'hold') parts.push(`${body.held ?? n} rep price${s(body.held ?? n)} held — in Held rep prices`);
      else parts.push(`${n} rep price${s(n)} imported — shown as "Rep reply" on each line`);
      if (body.kept) parts.push(`${body.kept} kept their confirmed price (rep's not stored)`);
      if (body.used_contract) parts.push(`${body.used_contract} set to the contract price`);
      const made = (body.contracts || []).filter(c => c.ok).length;
      if (made) parts.push(`${made} contract${s(made)} written`);
      const reqs = body.contract_requests || {};
      if (reqs.recorded) parts.push(`${reqs.recorded} line${s(reqs.recorded)} kept as the rep asking for a contract ("Contract asked for")`);
      if (reqs.not_ready) parts.push('the rep\'s contract requests were not kept — migration rep_contract_requests.sql has not run');
      const undoItems = confirmedNow.map(r => ({ key: r.key, side: r.side || 'sale' }));
      const notDone = notDoneList({ rows, ticked, attention, results, contracts: body.contracts || [] });
      // Anything not priced is said, line by line, before the window closes.
      if (notDone.some(x => x.kind !== 'unticked' && x.kind !== 'block')) {
        setResult({ done: `Done: ${parts.join('; ')}.`, notDone });
        setBusy(false);
        if (onDone) onDone(null, undoItems, true);
        return;
      }
      onDone(parts.join('; '), undoItems);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  // Approve / Hold / Keep as options: with lines needing attention, say how
  // many are left out first (Cam, 21 Oct 2026 — "still allow the user to click
  // accept / import, but clearly show the things that need attention").
  const start = (action) => {
    if (leftOut) setAsking({ action });
    else save(action);
  };

  const what = `${lineCount} line${lineCount === 1 ? '' : 's'}`;
  const actionWord = (a) => (a === 'approve' ? 'Approve' : a === 'hold' ? 'Hold' : 'Keep as options');
  const footer = result ? (
    <div className="buttons slideout">
      <div>
        <button type="button" className="btn-slideout" onClick={onClose}>
          <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
          <span className="btn-label">Close</span>
        </button>
      </div>
    </div>
  ) : (
    <div className="buttons slideout">
      <div>
        <button type="button" className="btn-slideout" onClick={onClose}>
          <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
          <span className="btn-label">Cancel</span>
        </button>
      </div>
      {read && (
        <div>
          <button type="button" className="btn-slideout" onClick={() => start(null)} disabled={busy || !lineCount}
            title={lineCount ? `Import as an option on each line, decided later${leftOut ? ` — ${leftOut} needing attention left out` : ''}` : 'Nothing ready to import — tick a line with a price, or put right what needs attention'}>
            <div className="icon-wrapper"><span className="material-symbols-rounded">download_done</span></div>
            <span className="btn-label">Keep as options ({what})</span>
          </button>
        </div>
      )}
      {read && (
        <div>
          <button type="button" className="btn-slideout" onClick={() => start('hold')} disabled={busy || !lineCount || !reviewReady}
            title={!reviewReady ? 'Not ready — migration pricing_costing_rep_review.sql has not run' : `Into Held rep prices — approve later or send back for a second check${leftOut ? ` — ${leftOut} needing attention left out` : ''}`}>
            <div className="icon-wrapper"><span className="material-symbols-rounded">pause_circle</span></div>
            <span className="btn-label">Hold ({what})</span>
          </button>
        </div>
      )}
      {read && (
        <div>
          <button type="button" className="btn-slideout" onClick={() => start('approve')} disabled={busy || !lineCount || !reviewReady}
            title={!reviewReady ? 'Not ready — migration pricing_costing_rep_review.sql has not run' : `Confirm each ticked line's sale price at the rep's price${leftOut ? ` — ${leftOut} needing attention left out` : ''}`}>
            <div className="icon-wrapper"><span className="material-symbols-rounded">thumb_up</span></div>
            <span className="btn-label">{busy ? 'Saving…' : `Approve (${what})`}</span>
          </button>
        </div>
      )}
    </div>
  );

  return (
    <Modal isOpen onClose={onClose} title="Import rep replies" size="xl" className="rep-replies" footer={footer}>
      {result ? <ResultBody result={result} /> : (
        <ImportBody read={read} rows={rows} busy={busy} error={error} ticked={ticked} setTicked={setTicked}
          openRows={openRows} setOpenRows={setOpenRows} readFile={readFile} lineCount={lineCount}
          conflicts={conflicts} answers={answers} setAnswers={setAnswers} offers={offers} setOffers={setOffers}
          shared={shared} setShared={setShared} attention={attention}
          attentionOnly={attentionOnly} showAttention={showAttention} pinned={pinned} />
      )}
      <ConfirmDialog
        isOpen={!!asking}
        title="Some lines need attention"
        type="warning"
        className="rr-attn-dialog"
        message={asking ? (
          <div className="rr-attn-ask">
            <p>{leftOut} line{leftOut === 1 ? ' needs' : 's need'} attention and will be left out — nothing is written for {leftOut === 1 ? 'it' : 'them'}:</p>
            <ul>{[...new Set(Object.values(attention.lines))].slice(0, 4).map(t => <li key={t}>{t}</li>)}</ul>
            <p>{actionWord(asking.action)} the other {what}?</p>
          </div>
        ) : null}
        confirmText={asking ? `${actionWord(asking.action)} ${what}` : ''}
        confirmIcon={asking?.action === 'approve' ? 'thumb_up' : asking?.action === 'hold' ? 'pause_circle' : 'download_done'}
        cancelText="Go back"
        onConfirm={() => save(asking.action)}
        onCancel={() => setAsking(null)} />
    </Modal>
  );
}

/** After the save: what was done, and every line not priced with its order. */
function ResultBody({ result }) {
  useSlideOutButton([result]);
  const problems = result.notDone.filter(x => x.kind !== 'unticked' && x.kind !== 'block');
  const quiet = result.notDone.filter(x => x.kind === 'unticked' || x.kind === 'block');
  const lines = new Set(problems.map(x => String(x.id))).size;
  return (
    <div className="rr-result">
      <p className="pc-flag info rr-done"><span className="material-symbols-rounded">check_circle</span>{result.done}</p>
      <h5 className="rr-result-head"><span className="material-symbols-rounded">error</span>{lines} line{lines === 1 ? '' : 's'} not priced{problems.length > lines ? ` (${problems.length} reasons)` : ''}</h5>
      <NotDoneTable list={problems} error />
      {quiet.length > 0 && (
        <>
          <h5 className="rr-result-head quiet">Also not imported: {quiet.length} line{quiet.length === 1 ? '' : 's'} (left unticked, invoiced or gone)</h5>
          <NotDoneTable list={quiet} />
        </>
      )}
    </div>
  );
}

function NotDoneTable({ list, error = false }) {
  return (
    <table className={`rr-line-table rr-notdone ${error ? 'err' : ''}`}>
      <thead>
        <tr><th>SO</th><th>PO</th><th>Customer</th><th>Delivery</th><th>Product</th><th className="num">Qty</th><th>Why</th></tr>
      </thead>
      <tbody>
        {list.map(x => (
          <tr key={`${x.id}|${x.why}`}>
            <td>{x.order_number || (x.missing ? `Line ${x.id}` : '—')}</td>
            <td>{x.po_number || <span className="pc-muted">from stock</span>}</td>
            <td>{x.account_no ? <span className="account-badge-inline">{x.account_no}</span> : null} {x.customer || ''}</td>
            <td>{x.delivery_date ? formatDate(x.delivery_date) : ''}</td>
            <td>{[x.product_code, x.product].filter(Boolean).join(' · ')}</td>
            <td className="num">{qtyText(x)}</td>
            <td className="rr-notdone-why">{x.why}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** The window's body; mounts with the footer, so it measures the slide-out buttons. */
function ImportBody({ read, rows, busy, error, ticked, setTicked, openRows, setOpenRows, readFile, lineCount,
  conflicts, answers, setAnswers, offers, setOffers, shared, setShared, attention, attentionOnly, showAttention, pinned }) {
  const [dragOver, setDragOver] = useState(false);
  const need = attention.count;
  useSlideOutButton([read, busy, lineCount, need, attentionOnly]);
  const asked = Object.keys(conflicts).filter(k => ticked[k]);
  const unanswered = asked.filter(k => !answers[k]).length;
  const answerAll = (want) => setAnswers(prev => {
    const next = { ...prev };
    asked.forEach(k => { const a = bulkConflictAnswer(conflicts[k], want); if (a) next[k] = a; });
    return next;
  });
  const summary = read ? writeSummary({ rows, ticked, answers, conflicts, offers, shared, action: null }) : [];
  const setOffer = (i, patch) => setOffers(prev => ({ ...prev, [i]: { mode: 'new', scope: 'parent', ...(prev[i] || {}), ...patch } }));
  const setSharedOf = (label, patch) => setShared(prev => ({ ...prev, [label]: { ...(prev[label] || {}), ...patch } }));
  const join = (i, j) => {
    const next = joinContract({ offers, shared }, i, j);
    setOffers(next.offers);
    setShared(next.shared);
  };
  const members = sharedMembers(rows, offers);
  const priced = rows.filter(r => r.price).length;
  const changed = rows.reduce((n, r) => n + r.lines.filter(l => !l.block && !l.check.ok).length, 0);
  // "Tick all" = every priced row's lines with nothing changed.
  const goodKeys = rows.flatMap((r, i) => (r.price ? r.lines.filter(l => !l.block && l.check.ok).map(l => lineKey(i, l.id)) : []));
  const allTicked = goodKeys.length > 0 && goodKeys.every(k => ticked[k]);
  const setKeys = (keys, on) => setTicked(prev => ({ ...prev, ...Object.fromEntries(keys.map(k => [k, on])) }));
  const attnRows = Object.keys(attention.rows).length;
  const shown = attentionOnly ? new Set(pinned) : null;
  return (
    <>
      {!read && (
        <label
          className={`pc-drop ${dragOver ? 'over' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); readFile(e.dataTransfer.files?.[0]); }}
        >
          <span className="material-symbols-rounded">upload_file</span>
          <span>Drop the rep's spreadsheet here, or click to choose it</span>
          <span className="pc-muted">The "Price request" workbook the system sent them, with "Your price" filled in</span>
          <input type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" hidden
            data-testid="rep-replies-file" onChange={(e) => readFile(e.target.files?.[0])} />
        </label>
      )}
      {busy && !read && <LoadingSpinner />}
      {error && <p className="pc-flag err rr-error"><span className="material-symbols-rounded">error</span>{error}</p>}
      {read && (
        <>
          <p className="pc-muted">{read.file_name} — {priced} price{priced === 1 ? '' : 's'} filled in of {rows.length} row{rows.length === 1 ? '' : 's'}. Each row is one request to the rep, covering the order lines under it. Tick all or some (a row, or its lines one by one), then Approve (confirms each ticked line's sale price at the rep's), Hold (into Held rep prices) or Keep as options (a "Rep reply" on each line, decided later). Anything marked as needing attention is left out until it is put right. Nothing changes on the orders except by Approve.</p>
          <div className="rr-counts">
            {(need > 0 || attentionOnly) && (
              <button type="button" className={`pc-chip rr-attn-chip ${attentionOnly ? 'on' : ''}`} aria-pressed={attentionOnly}
                onClick={() => showAttention(true)}
                title={need ? 'Show only the rows that need something before they can be written' : 'Nothing needs attention now'}>
                <span className="material-symbols-rounded">error</span>Needs attention ({need})
              </button>
            )}
            {attentionOnly && (
              <button type="button" className="pc-chip" onClick={() => showAttention(false)} title="Show every row of the spreadsheet">
                <span className="material-symbols-rounded">list</span>Show all ({rows.length})
              </button>
            )}
            <span className="pc-chip on"><span className="material-symbols-rounded">check_box</span>{lineCount} line{lineCount === 1 ? '' : 's'} ready</span>
            {changed > 0 && <span className="pc-chip"><span className="material-symbols-rounded">warning</span>{changed} line{changed === 1 ? '' : 's'} changed since the request — unticked, with why</span>}
            {asked.length > 0 && <span className="pc-chip"><span className="material-symbols-rounded">help</span>{unanswered ? `${unanswered} of ${asked.length} question${asked.length === 1 ? '' : 's'} to answer` : `${asked.length} question${asked.length === 1 ? '' : 's'} answered`}</span>}
            {asked.length > 0 && <button type="button" className="pc-chip" onClick={() => answerAll('keep')} title="Every ticked line confirmed here keeps its confirmed price (the rep's is not stored for it)"><span className="material-symbols-rounded">lock</span>Keep every confirmed price</button>}
            {asked.length > 0 && <button type="button" className="pc-chip" onClick={() => answerAll('rep')} title="Every ticked line with a question takes the rep's price (stored and confirmed)"><span className="material-symbols-rounded">person</span>Use every rep price</button>}
          </div>
          {need > 0 && (
            <p className="pc-flag err rr-attn-sum"><span className="material-symbols-rounded">error</span>
              {need} line{need === 1 ? '' : 's'} in {attnRows} row{attnRows === 1 ? '' : 's'} need{need === 1 ? 's' : ''} attention — {need === 1 ? 'it is' : 'they are'} left out of the import until put right; everything else can be imported now.
            </p>
          )}
          {attentionOnly && !need && <p className="pc-flag info"><span className="material-symbols-rounded">check_circle</span>Nothing needs attention now — Show all to see every row.</p>}
          {read.problems.map((p, i) => <p key={i} className="pc-flag warn"><span className="material-symbols-rounded">warning</span>{p}</p>)}
          <table className="data-table">
            <thead>
              <tr>
                <th className="checkbox-col">
                  <TickCheckbox id="rr-all" checked={allTicked} disabled={!goodKeys.length} ariaLabel="Tick every line with a price and nothing changed"
                    onChange={(e) => setKeys(goodKeys, e.target.checked)} />
                </th>
                <th>Customer</th><th>Product</th><th className="num">Asked at (cost)</th><th>Orders</th>
                <th className="num">Rep's price</th><th>Notes</th><th aria-label="Show the lines" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                if (shown && !shown.has(i)) return null;
                const usable = r.lines.filter(l => !l.block);
                const keys = usable.map(l => lineKey(i, l.id));
                const tickedHere = keys.filter(k => ticked[k]);
                const block = !r.price ? (r.priceText ? `"${r.priceText}" is not a price` : 'No price filled in')
                  : !usable.length ? 'Its lines are gone or invoiced' : null;
                const flagged = usable.filter(l => !l.check.ok).length;
                const open = !!openRows[i];
                const qKeys = keys.filter(k => conflicts[k] && ticked[k]);
                const qSigs = [...new Set(qKeys.map(k => conflicts[k].key))];
                const offer = r.contract_offer;
                const orderNos = [...new Set(r.lines.map(l => l.order_number).filter(Boolean))];
                const attn = attention.rows[i] || null;
                const sorted = shown && !attn;
                return (
                  <React.Fragment key={i}>
                    <tr className={`rr-group ${block ? 'pc-dim' : ''} ${attn ? 'rr-attn' : ''}`}>
                      <td className="checkbox-col">
                        <TickCheckbox id={`rr-${i}`} checked={!block && keys.length > 0 && tickedHere.length === keys.length} disabled={!!block}
                          title={block || (flagged ? 'Ticks every line of this row, the changed ones too' : 'Ticks every line of this row')}
                          ariaLabel={`Tick row ${r.row}`}
                          onChange={(e) => setKeys(keys, e.target.checked)} />
                      </td>
                      <td><div className="rr-what"><span>{r.customer || r.lines.find(l => l.customer)?.customer || '—'}</span>{r.accountNo && <span className="pc-muted">{r.accountNo}</span>}</div></td>
                      <td><div className="rr-what"><span>{r.product || r.lines.find(l => l.product)?.product || '—'}</span><span className="pc-muted">{[r.itemCode, r.unit].filter(Boolean).join(' · ')}{r.quantity ? ` · ${r.quantity} in total` : ''}</span></div></td>
                      <td className="num">{r.costText || <span className="pc-muted">{r.hasCostColumn ? '—' : 'not on the sheet'}</span>}</td>
                      <td>
                        <div className="rr-what">
                          <span>{r.lines.length} line{r.lines.length === 1 ? '' : 's'} · {orderNos.length} order{orderNos.length === 1 ? '' : 's'}</span>
                          <span className="pc-muted">{orderNos.slice(0, 4).join(', ')}{orderNos.length > 4 ? ` +${orderNos.length - 4}` : ''}</span>
                          {flagged > 0 && <span className="rr-why">{flagged} changed since — see the lines</span>}
                          {tickedHere.length > 0 && tickedHere.length < keys.length && <span className="pc-muted">{tickedHere.length} of {keys.length} ticked</span>}
                        </div>
                      </td>
                      <td className="num">{r.price ? money(r.price) : <span className="pc-muted">{block}</span>}</td>
                      <td>{r.notes}</td>
                      <td>
                        <button type="button" className="btn-icon" aria-expanded={open} aria-label={open ? `Hide the lines of row ${r.row}` : `Show the lines of row ${r.row}`}
                          title={open ? 'Hide the lines' : 'Show the lines to tick them one by one'}
                          onClick={() => setOpenRows(prev => ({ ...prev, [i]: !prev[i] }))}>
                          <span className="material-symbols-rounded">{open ? 'expand_less' : 'expand_more'}</span>
                        </button>
                      </td>
                    </tr>
                    {(attn || sorted || qKeys.length > 0 || (offer && r.price)) && (
                      <tr className={`rr-extra ${attn ? 'rr-attn' : ''}`}>
                        <td />
                        <td colSpan={7}>
                          {attn && (
                            <div className="rr-attn-note" role="note">
                              <span className="material-symbols-rounded">error</span>
                              <span>Needs attention — not written until put right: {attn.join('; ')}.</span>
                            </div>
                          )}
                          {sorted && <p className="pc-flag info"><span className="material-symbols-rounded">check_circle</span>Put right — this row no longer needs attention.</p>}
                          {qKeys.length > 0 && (
                            <QuestionPanel conflict={conflicts[qKeys[0]]} differs={qSigs.length > 1} price={r.price} count={qKeys.length}
                              answer={qKeys.every(k => answers[k] && answers[k] === answers[qKeys[0]]) ? answers[qKeys[0]] : null}
                              onAnswer={(a) => {
                                setAnswers(prev => {
                                  const next = { ...prev };
                                  qKeys.forEach(k => { if (conflicts[k].choices.includes(a)) next[k] = a; });
                                  return next;
                                });
                              }}
                              onOpen={() => setOpenRows(prev => ({ ...prev, [i]: true }))} />
                          )}
                          {offer && r.price && (
                            <ContractFromPricesPanel row={r} index={i} rows={rows} offer={offer} state={offers[i]} offers={offers} shared={shared}
                              request={r.contract_requested ? `marked "Make this a contract? (Y)" on the spreadsheet${r.contract_requested.note ? `: "${r.contract_requested.note}"` : ''} — still your decision` : null}
                              members={offers[i]?.shared ? (members.get(offers[i].shared) || [i]) : [i]}
                              problem={contractProblem(i, rows, offers, shared)}
                              onChange={(patch) => setOffer(i, patch)}
                              onShared={(patch) => (offers[i]?.shared ? setSharedOf(offers[i].shared, patch) : setOffer(i, patch))}
                              onJoin={(j) => join(i, j)} onLeave={(k) => join(k, null)} />
                          )}
                        </td>
                      </tr>
                    )}
                    {open && (
                      <tr className="rr-lines">
                        <td />
                        <td colSpan={7}>
                          <table className="rr-line-table">
                            <tbody>
                              {r.lines.map(l => {
                                const k = lineKey(i, l.id);
                                const lineAttn = attention.short[k] || null;
                                return (
                                  <tr key={l.id} className={`${l.block ? 'pc-dim' : ''} ${lineAttn ? 'rr-attn-line' : ''}`}>
                                    <td className="rr-tick">
                                      <TickCheckbox id={`rr-${i}-${l.id}`} checked={!l.block && !!ticked[k]} disabled={!!l.block || !r.price}
                                        title={l.block || (!r.price ? 'No price on this row' : '')} ariaLabel={`Tick ${l.order_number || `line ${l.id}`}`}
                                        onChange={(e) => setKeys([k], e.target.checked)} />
                                    </td>
                                    <td>{l.missing ? `Line ${l.id}` : l.order_number}{l.po_number && <div className="pc-muted">{l.po_number}</div>}</td>
                                    <td>{l.missing ? '' : formatDate(l.delivery_date)}</td>
                                    <td>{l.missing ? '' : qtyText(l)}</td>
                                    <td className="num">{l.missing ? '' : (l.current_price ? `${money(l.current_price)} on the order` : 'no price on the order')}</td>
                                    <td>
                                      {lineAttn && <div className="rr-attn-why">{lineAttn}</div>}
                                      {l.check.reasons.map(t => <div key={t} className="rr-why">{t}</div>)}
                                      {l.check.notes.map(t => <div key={t} className="rr-note">{t}</div>)}
                                      {l.check.ok && !l.check.notes.length && !conflicts[k] && <span className="pc-muted">Unchanged</span>}
                                      {conflicts[k] && ticked[k] && (
                                        <LineQuestion conflict={conflicts[k]} price={r.price} answer={answers[k] || null}
                                          label={l.order_number || `line ${l.id}`}
                                          onAnswer={(a) => setAnswers(prev => ({ ...prev, [k]: a }))} />
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          {summary.length > 0 && (
            <div className="rr-summary" aria-label="What will be written">
              <h5><span className="material-symbols-rounded">fact_check</span>What will be written</h5>
              <ul>{summary.map(t => <li key={t}>{t}</li>)}</ul>
              {need > 0 && <p className="rr-attn-why">{need} line{need === 1 ? '' : 's'} needing attention {need === 1 ? 'is' : 'are'} left out.</p>}
              <p className="pc-muted">The last line follows the button you press: Approve confirms the rep's price, Hold holds it, Keep as options keeps it as a "Rep reply". Nothing is written until you press one.</p>
            </div>
          )}
        </>
      )}
    </>
  );
}

/** The three answers, as chips. */
function Answers({ choices, answer, onAnswer, label }) {
  return (
    <div className="rr-answers" role="radiogroup" aria-label={label}>
      {choices.map(c => (
        <button key={c} type="button" role="radio" aria-checked={answer === c} className={`pc-chip ${answer === c ? 'on' : ''}`}
          onClick={() => onAnswer(c)}>
          {REP_CONFLICT_LABELS[c]}
        </button>
      ))}
    </div>
  );
}

/** What the question is about, in words. */
function conflictWords(conflict, price) {
  return [
    conflict.confirmed ? confirmedText(conflict.confirmed) : null,
    conflict.contract ? `Contract now ${money(conflict.contract.value)}${conflict.contract.ref ? ` (${conflict.contract.ref})` : ''}` : null,
    `Rep's price ${money(price)}`
  ].filter(Boolean).join(' · ');
}

/** A row's question for all its lines — or, where they differ, a pointer to each. */
function QuestionPanel({ conflict, differs, price, count, answer, onAnswer, onOpen }) {
  const choices = differs ? ['keep', 'contract', 'rep'].filter(c => c !== 'contract' || conflict.contract) : conflict.choices;
  return (
    <div className="rr-question">
      <span className="material-symbols-rounded">help</span>
      <div>
        <div>{differs ? `${count} lines already have a price — they differ; answer for all here, or each line below.` : `${count === 1 ? 'This line has' : `These ${count} lines have`} a price already: ${conflictWords(conflict, price)}`}</div>
        <Answers choices={choices} answer={answer} onAnswer={onAnswer} label="Which price to keep" />
        {differs && <button type="button" className="pc-link" onClick={onOpen}>Show the lines</button>}
      </div>
    </div>
  );
}

function LineQuestion({ conflict, price, answer, onAnswer, label }) {
  return (
    <div className="rr-line-question">
      <div className="rr-why">{conflictWords(conflict, price)}</div>
      <Answers choices={conflict.choices} answer={answer} onAnswer={onAnswer} label={`Which price to keep for ${label}`} />
    </div>
  );
}

export default RepRepliesImportModal;
